import React from 'react';
import { X, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';

export default function GEDESSUploadModal(ctx) {
  const {
    showUploadModal, setShowUploadModal,
    dragFiles, setDragFiles,
    dragPreviews, setDragPreviews,
    uploadForm, setUploadForm,
    handleUpload, handleDragUpload,
    showInlineNewCat, setShowInlineNewCat,
    newCatLabel, setNewCatLabel, handleInlineCreateCategory,
    getMergedCategories, allEntities, userRole, uploading,
  } = ctx;

  if (!showUploadModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{dragFiles.length > 0 ? `Ajouter ${dragFiles.length} document(s)` : 'Ajouter un document'}</CardTitle>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setShowUploadModal(false); setDragFiles([]); setDragPreviews(prev => { prev.forEach(p => p.preview && URL.revokeObjectURL(p.preview)); return []; }); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          {dragPreviews.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="drag-preview-area">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fichiers selectionnes</label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {dragPreviews.map((p, i) => (
                  <div key={i} className={cn("flex items-center gap-2 p-2 rounded-lg border", p.info.bg, p.info.borderColor)}>
                    {p.preview ? (
                      <img src={p.preview} alt="" className="w-10 h-10 object-cover rounded" />
                    ) : (
                      <div className={cn("w-10 h-10 rounded flex items-center justify-center", p.info.bg)}>
                        <p.info.icon className={cn("w-5 h-5", p.info.color)} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{p.file.name}</p>
                      <p className="text-[10px] text-gray-500">{(p.file.size / 1024).toFixed(0)} Ko</p>
                    </div>
                    <button onClick={() => {
                      if (p.preview) URL.revokeObjectURL(p.preview);
                      setDragPreviews(prev => prev.filter((_, j) => j !== i));
                      setDragFiles(prev => prev.filter((_, j) => j !== i));
                    }} className="text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); if (dragFiles.length > 0) { handleDragUpload(uploadForm.entite, uploadForm.categorie, uploadForm.description); } else { handleUpload(e); } }} className="space-y-4">
            {dragFiles.length === 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier *</label>
                <input type="file" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required data-testid="file-input" />
                <p className="text-xs text-gray-500 mt-1">PDF, Images, Word, Excel (max 20 Mo)</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entite ESS *</label>
              <select value={uploadForm.entite} onChange={(e) => { setUploadForm({ ...uploadForm, entite: e.target.value, categorie: '' }); setShowInlineNewCat(false); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required data-testid="entity-select">
                <option value="">Selectionner l'entite</option>
                {allEntities.map(entity => (<option key={entity.id} value={entity.id}>{entity.name} – {entity.badge}</option>))}
              </select>
            </div>

            {uploadForm.entite && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Categorie *</label>
                  {userRole === 'super_admin' && (
                    <button
                      type="button"
                      onClick={() => setShowInlineNewCat(!showInlineNewCat)}
                      className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-0.5"
                      data-testid="inline-add-category-btn"
                    >
                      <Plus className="w-3 h-3" /> Nouvelle
                    </button>
                  )}
                </div>
                {showInlineNewCat ? (
                  <div className="flex gap-2 items-center" data-testid="inline-category-form">
                    <Input
                      value={newCatLabel}
                      onChange={(e) => setNewCatLabel(e.target.value)}
                      placeholder="Nom de la categorie"
                      className="h-9 text-sm flex-1"
                      data-testid="inline-new-cat-name"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInlineCreateCategory(); } }}
                    />
                    <Button type="button" size="sm" className="h-9 bg-amber-600 hover:bg-amber-700 text-xs px-3" onClick={handleInlineCreateCategory} data-testid="inline-confirm-cat">OK</Button>
                    <Button type="button" size="sm" variant="ghost" className="h-9 text-xs px-2" onClick={() => { setShowInlineNewCat(false); setNewCatLabel(''); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <select value={uploadForm.categorie} onChange={(e) => setUploadForm({ ...uploadForm, categorie: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required data-testid="category-select">
                    <option value="">Selectionner la categorie</option>
                    {getMergedCategories(uploadForm.entite).map(cat => (<option key={cat.value} value={cat.value}>{cat.label}{cat.is_custom ? ' *' : ''}</option>))}
                  </select>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Description du document..." data-testid="description-input" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowUploadModal(false); setDragFiles([]); setDragPreviews([]); }} disabled={uploading}>Annuler</Button>
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={uploading} data-testid="submit-upload-btn">
                {uploading ? 'Upload...' : dragFiles.length > 1 ? `Ajouter ${dragFiles.length} fichiers` : 'Ajouter'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
